"use client";

import * as React from "react";
import { Badge } from "../../components/ui/badge";
import { Box } from "../../components/ui/box";
import { useFitCount } from "../../hooks/use-fit-count";
import type { Omittable } from "../../lib/props";

/**
 * One line of items, as many as FIT, then a "+N" chip for the rest.
 *
 * The visible count is space-driven, not a fixed cap: an invisible measuring
 * copy of every item (plus a worst-case "+N") renders under the row, and a
 * layout-effect pass greedily counts how many natural-width items fit while
 * reserving room for the "+N" whenever anything stays hidden. Re-measures on
 * container resize (a column drag, a panel resize). Built for chip clusters in
 * a list cell or a property row — anything that must hold one line.
 *
 * Two rules keep the row from flickering:
 *
 * - **Natural fit is the resting layout.** Whole items plus the counter, never
 *   squashed slivers at rest. An item earns its slot only if the "+N" for
 *   whatever remains after it still fits too — the counter must never be the
 *   thing that overflows.
 * - **Hysteresis absorbs a transient squeeze.** When the container shrinks
 *   below the current items' natural fit (hover actions claiming their slot, a
 *   small drag), the count holds as long as those items can absorb the deficit
 *   by squashing to `minItemWidth` — they truncate in place instead of popping
 *   into "+N", and restore when the room comes back. Items therefore need to
 *   be able to shrink and truncate themselves.
 *
 * Always at least one item: it shrinks and truncates rather than vanishing.
 */

export type FitRowProps = Omittable<{
  /** The items, keyed. Each must be able to shrink and truncate its own label. */
  children: React.ReactNode;
  /** Gap between items, in px. Applied in the fit math too. @default 4 */
  gap?: number;
  /** How far an already-visible item may squash before it re-buckets into
   *  "+N" — the shock absorber for transient narrowing. @default 40 */
  minItemWidth?: number;
  /** The overflow chip. Default: the quiet colorless `+N` value chip. */
  renderOverflow?: (hidden: number) => React.ReactNode;
  className?: string;
}>;

const defaultOverflow = (hidden: number) => (
  <Badge size="tag">{`+${hidden}`}</Badge>
);

export function FitRow({
  children,
  gap = 4,
  minItemWidth = 40,
  renderOverflow = defaultOverflow,
  className,
}: FitRowProps): React.ReactElement | null {
  const items = React.Children.toArray(children);
  const total = items.length;
  // The measuring copy is keyed off the items' identity, so a changed set
  // re-measures and an identical re-render does not.
  const signature = items
    .map((item) => (React.isValidElement(item) ? String(item.key) : ""))
    .join("\u0000");

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const measureRef = React.useRef<HTMLDivElement | null>(null);

  const visibleCount = useFitCount(containerRef, {
    signature: `${signature}\u0000${total}`,
    gap,
    minItemWidth,
    min: 1,
    measure: () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return null;
      const nodes = Array.from(measure.children) as HTMLElement[];
      if (nodes.length < 2) return null;
      // Last measure child = the widest-possible "+N" sample.
      const plusWidth = nodes[nodes.length - 1]!.getBoundingClientRect().width;
      plusWidthRef.current = plusWidth;
      return {
        widths: nodes.slice(0, -1).map((node) => node.getBoundingClientRect().width),
        capacity: container.getBoundingClientRect().width,
      };
    },
    reserve: () => gap + plusWidthRef.current,
  });
  const plusWidthRef = React.useRef(0);

  if (total === 0) return null;
  const visible = items.slice(0, Math.min(visibleCount, total));
  const hidden = total - visible.length;

  return (
    <Box
      ref={containerRef}
      data-slot="fit-row"
      // The row must span the full available width — measurement reads the
      // container's own box, so a content-hugging box would report only what
      // already fits.
      fill="flex"
      position="relative"
      direction="row"
      align="center"
      gap={gap}
      minWidth={0}
      overflow="hidden"
      className={className}
    >
      {visible}
      {hidden > 0 ? renderOverflow(hidden) : null}
      {/* Invisible measuring copy: every item at natural width plus the
          worst-case counter. Absolute (no layout footprint), transparent,
          inert to pointers and to the accessibility tree. Gap is applied in
          the fit math, not here. */}
      <Box
        ref={measureRef}
        aria-hidden
        position="absolute"
        top={0}
        left={0}
        direction="row"
        align="center"
        opacity={0}
        pointerEvents="none"
      >
        {items}
        {renderOverflow(Math.max(1, total - 1))}
      </Box>
    </Box>
  );
}
